import React from "react";
import { Container, Grid, Typography, Box } from "@mui/material";
import { useT } from "contexts/TranslationContext";

const PRIMARY_GREEN = '#34986C'; 

// --- Process Step Component ---
const ProcessStep = ({ number, title, description, isLast }) => {
    return (
        <Box display="flex" mb={isLast ? 5 : 0}>
            {/* Step Number and Vertical Line */}
            <Box display="flex" flexDirection="column" alignItems="center" mr={2}>
                {/* Number Circle */}
                <Box
                    sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        bgcolor: PRIMARY_GREEN,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                    }}
                >
                    {number}
                </Box>
                
                {/* Vertical Connector Line (Hidden for the last step) */}
                {!isLast && (
                    <Box
                        sx={{
                            width: '2px',
                            height: 100, // Fixed height for vertical line segment
                            bgcolor: '#d1e7c5', // Very light green color for the line
                        }}
                    />
                )}
            </Box>

            {/* Content Text */}
            <Box pt={0.25}>
                <Typography 
                    variant="body1" 
                    sx={{ 
                        fontWeight: 700, 
                        color: 'grey.900', 
                        lineHeight: 1.2,
                        mb: 0.5
                    }}
                >
                    {title}
                </Typography>
                <Typography 
                    variant="body2" 
                    sx={{ color: 'grey.700' }}
                >
                    {description}
                </Typography>
            </Box>
        </Box>
    );
};

// --- Process Section Component ---
const ProcessSection = () => {
    const t = useT();
    const steps = [
        {
            title: t("steps.step1"),
            description: t("steps.step1Desc"),
        },
        {
            title: t("steps.step2"),
            description: t("steps.step2Desc"),
        },
        {
            title: t("steps.step3"),
            description: t("steps.step3Desc"),
        },
    ];

    return (
      <Grid container justifyContent="center" sx={{ bgcolor: '#f4fbf7' }}>
        <Box 
            component="section"
            sx={{
                pt: { xs: 8, md: 12 }, // Top padding
                pb: { xs: 10, md: 16 }, // Bottom padding
                px: { xs: 4, sm: 8, lg: 16 },
                width: '100%',
                maxWidth: 1000, 
                mx: 'auto' // Center the box
            }}
        >
            {/* Main Title */}
            <Typography
                variant="h4"
                component="h2"
                textAlign="center"
                sx={{
                    fontWeight: 800,
                    color: 'grey.900',
                    mb: 3,
                    fontSize: { xs: '2rem', sm: '2.5rem' }
                }}
            >
                {t("steps.title")}
            </Typography>

            {/* Subtitle/Intro Text */}
            <Typography
                variant="body1"
                textAlign="center"
                sx={{
                    color: 'grey.700',
                    maxWidth: 700,
                    mx: 'auto', // Center the text
                    mb: { xs: 8, md: 10 } // Margin before the process steps
                }}
            >
                {t("steps.subtitle")}
            </Typography>

            {/* Process Steps List (Centered using a Grid for responsive container width) */}
            <Grid container justifyContent="center">
                <Grid item xs={12} sm={8} md={6} lg={4}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                        {steps.map((step, index) => (
                            <ProcessStep 
                                key={index}
                                number={index + 1}
                                title={step.title}
                                description={step.description}
                                isLast={index === steps.length - 1}
                            />
                        ))}
                    </Box>
                </Grid>
            </Grid>
        </Box>
      </Grid>
    );
};
export default ProcessSection;